import html2canvas from 'html2canvas';

/**
 * Generates a thumbnail from a canvas element
 * @param {HTMLElement} canvasElement - The canvas element to capture
 * @param {Object} options - Options for thumbnail generation
 * @param {number} options.width - Target width of the thumbnail
 * @param {number} options.height - Target height of the thumbnail
 * @returns {Promise<string>} - Base64 encoded thumbnail image
 */
export async function generateThumbnail(canvasElement, options = {}) {
  try {
    console.log('📸 [ThumbnailGenerator] Starting thumbnail generation for canvas element:', canvasElement);
    
    // Enhanced error checking
    if (!canvasElement) {
      console.error('📸 [ThumbnailGenerator] Error: No canvas element provided');
      return null;
    }
    
    if (!canvasElement.isConnected) {
      console.error('📸 [ThumbnailGenerator] Error: Canvas element is not connected to the DOM');
      return null;
    }
    
    console.log('📸 [ThumbnailGenerator] Canvas element details:', {
      id: canvasElement.id,
      className: canvasElement.className,
      width: canvasElement.offsetWidth,
      height: canvasElement.offsetHeight,
      childCount: canvasElement.childNodes.length,
      isVisible: canvasElement.offsetParent !== null,
      hasChildren: canvasElement.children.length > 0,
      style: {
        position: canvasElement.style.position,
        display: canvasElement.style.display,
        background: canvasElement.style.backgroundColor || canvasElement.style.background,
        overflow: canvasElement.style.overflow
      }
    });
    
    // Ensure canvas has dimensions
    if (canvasElement.offsetWidth <= 0 || canvasElement.offsetHeight <= 0) {
      console.error('📸 [ThumbnailGenerator] Error: Canvas has zero width or height');
      return null;
    }
    
    // Default options - use larger size for better quality
    const { width = 600, height = 450 } = options;
    console.log('📸 [ThumbnailGenerator] Using dimensions:', { width, height });

    // Create a temporary preview container
    const previewContainer = document.createElement('div');
    previewContainer.style.position = 'absolute';
    previewContainer.style.left = '-9999px';
    previewContainer.style.width = `${width}px`;
    previewContainer.style.height = `${height}px`;
    previewContainer.style.backgroundColor = '#ffffff';
    previewContainer.style.overflow = 'hidden';
    previewContainer.style.zIndex = '-1000'; // Make sure it's below everything
    document.body.appendChild(previewContainer);
    console.log('📸 [ThumbnailGenerator] Created preview container');

    // Clone the canvas for preview with deep cloning
    const previewCanvas = canvasElement.cloneNode(true);
    previewCanvas.style.width = '100%';
    previewCanvas.style.height = '100%';
    previewCanvas.style.transform = 'none';
    previewCanvas.style.position = 'relative';
    previewCanvas.style.overflow = 'hidden';
    previewCanvas.style.display = 'block'; // Ensure it's visible
    previewCanvas.style.margin = '0'; // Reset margins
    previewCanvas.style.padding = '0'; // Reset padding
    previewContainer.appendChild(previewCanvas);
    console.log('📸 [ThumbnailGenerator] Cloned canvas into preview container');
    
    // Ensure images are loaded in the cloned canvas
    const previewImages = previewCanvas.querySelectorAll('img');
    console.log(`📸 [ThumbnailGenerator] Found ${previewImages.length} images in cloned canvas`);
    
    // Wait for all images to load
    if (previewImages.length > 0) {
      console.log('📸 [ThumbnailGenerator] Waiting for images to load...');
      
      // Create load promises for all images
      const imageLoadPromises = Array.from(previewImages).map(img => {
        // If image is already loaded, return resolved promise
        if (img.complete && img.naturalWidth !== 0) {
          console.log('📸 [ThumbnailGenerator] Image already loaded:', img.src);
          return Promise.resolve();
        }
        
        // Otherwise wait for load or error
        return new Promise((resolve) => {
          img.onload = () => {
            console.log('📸 [ThumbnailGenerator] Image loaded:', img.src);
            resolve();
          };
          img.onerror = () => {
            console.warn('📸 [ThumbnailGenerator] Error loading image:', img.src);
            resolve(); // Resolve anyway to continue with thumbnail
          };
          
          // Ensure src attribute is set (in rare cases it might not be)
          if (!img.src) {
            console.warn('📸 [ThumbnailGenerator] Image has no src, setting to empty');
            img.src = '';
            resolve();
          }
        });
      });
      
      // Wait for all images with timeout
      try {
        const imageLoadTimeout = setTimeout(() => {
          console.warn('📸 [ThumbnailGenerator] Timed out waiting for images to load, continuing anyway');
        }, 2000);
        
        await Promise.race([
          Promise.all(imageLoadPromises),
          new Promise(resolve => setTimeout(resolve, 2000)) // 2 second timeout
        ]);
        
        clearTimeout(imageLoadTimeout);
      } catch (imageError) {
        console.warn('📸 [ThumbnailGenerator] Error waiting for images:', imageError);
        // Continue anyway
      }
    }
    
    // Additional delay to ensure everything is rendered
    await new Promise(resolve => setTimeout(resolve, 800));
    console.log('📸 [ThumbnailGenerator] All preparation complete, capturing thumbnail...');
    
    // Save original container styling
    const originalContainerStyling = {
      width: previewContainer.style.width,
      height: previewContainer.style.height,
      position: previewContainer.style.position,
      left: previewContainer.style.left,
      overflow: previewContainer.style.overflow
    };
    
    // Temporarily make container visible for better html2canvas performance
    previewContainer.style.position = 'fixed';
    previewContainer.style.left = '-5000px';
    
    // Use html2canvas to capture the preview
    console.log('📸 [ThumbnailGenerator] Invoking html2canvas...');
    console.time('html2canvas');
    try {
      const canvas = await html2canvas(previewCanvas, {
        width,
        height,
        scale: 2, // Higher scale for better quality
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false, // Disable logging for cleaner console
        imageTimeout: 10000, // Increased timeout for images to load
        foreignObjectRendering: false, // More reliable in some browsers
        removeContainer: false, // Let us handle cleanup
        onclone: (clonedDoc) => {
          console.log('📸 [ThumbnailGenerator] html2canvas cloned document');
          // Additional adjustments to ensure proper rendering
          const clonedCanvas = clonedDoc.querySelector('.canvas');
          if (clonedCanvas) {
            console.log('📸 [ThumbnailGenerator] Found cloned canvas, applying styles');
            // Ensure any backgrounds are applied properly
            const style = window.getComputedStyle(canvasElement);
            clonedCanvas.style.backgroundColor = style.backgroundColor;
            clonedCanvas.style.backgroundImage = style.backgroundImage;
            clonedCanvas.style.backgroundSize = style.backgroundSize;
            clonedCanvas.style.backgroundPosition = style.backgroundPosition;
          }
        }
      });
      console.timeEnd('html2canvas');
      console.log('📸 [ThumbnailGenerator] html2canvas completed, canvas created:', {
        width: canvas.width,
        height: canvas.height
      });

      // Convert to base64 - use PNG instead of JPEG for better quality
      const thumbnail = canvas.toDataURL('image/png', 0.95);
      console.log('📸 [ThumbnailGenerator] Generated thumbnail, length:', thumbnail.length);
      
      // Verify the thumbnail starts with the correct data URL prefix
      if (!thumbnail.startsWith('data:image/')) {
        console.error('❌ [ThumbnailGenerator] Invalid thumbnail format:', thumbnail.substring(0, 30));
        throw new Error('Invalid thumbnail format');
      }

      // Clean up
      document.body.removeChild(previewContainer);
      console.log('📸 [ThumbnailGenerator] Cleanup complete, thumbnail generated successfully');

      return thumbnail;
    } catch (html2canvasError) {
      console.error('❌ [ThumbnailGenerator] html2canvas error:', html2canvasError);
      
      // Try fallback approach
      console.log('📸 [ThumbnailGenerator] Attempting fallback thumbnail generation...');
      
      try {
        // Create a simple canvas for the fallback
        const fallbackCanvas = document.createElement('canvas');
        fallbackCanvas.width = width;
        fallbackCanvas.height = height;
        const ctx = fallbackCanvas.getContext('2d');
        
        // Draw something basic related to the original canvas
        ctx.fillStyle = '#f5f5f5';
        ctx.fillRect(0, 0, width, height);
        
        // Add border
        ctx.strokeStyle = '#e0e0e0';
        ctx.lineWidth = 8;
        ctx.strokeRect(4, 4, width - 8, height - 8);
        
        // Try to get any background color from the original canvas
        try {
          const computedStyle = window.getComputedStyle(canvasElement);
          const backgroundColor = computedStyle.backgroundColor;
          
          if (backgroundColor && backgroundColor !== 'transparent' && backgroundColor !== 'rgba(0, 0, 0, 0)') {
            // Draw a rectangle with the background color
            ctx.fillStyle = backgroundColor;
            ctx.fillRect(20, 20, width - 40, height - 40);
          }
        } catch (styleError) {
          console.warn('📸 [ThumbnailGenerator] Error getting background color:', styleError);
        }
        
        // Add text
        ctx.fillStyle = '#666666';
        ctx.font = 'bold 28px Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('Template Preview', width / 2, height / 2 - 20);
        ctx.font = '16px Arial, sans-serif';
        ctx.fillText('(Fallback thumbnail)', width / 2, height / 2 + 20);
        
        // Convert to data URL
        const fallbackThumbnail = fallbackCanvas.toDataURL('image/png');
        console.log('📸 [ThumbnailGenerator] Generated fallback thumbnail');
        
        // Clean up
        document.body.removeChild(previewContainer);
        
        return fallbackThumbnail;
      } catch (fallbackError) {
        console.error('❌ [ThumbnailGenerator] Fallback thumbnail generation failed:', fallbackError);
        
        // Clean up
        try {
          document.body.removeChild(previewContainer);
        } catch (cleanupError) {
          console.warn('📸 [ThumbnailGenerator] Error during cleanup:', cleanupError);
        }
        
        return null;
      }
    }
  } catch (error) {
    console.error('❌ [ThumbnailGenerator] Error generating thumbnail:', error);
    console.error('❌ [ThumbnailGenerator] Stack trace:', error.stack);
    
    // Try to clean up if previewContainer was created
    try {
      const previewContainer = document.querySelector('div[style*="-9999px"]');
      if (previewContainer) {
        document.body.removeChild(previewContainer);
      }
    } catch (cleanupError) {
      console.warn('❌ [ThumbnailGenerator] Error during emergency cleanup:', cleanupError);
    }
    
    return null;
  }
}

/**
 * Generates a screenshot of the entire visible preview page
 * @returns {Promise<string>} - Base64 encoded image data URL
 */
export async function captureFullPageScreenshot() {
  try {
    console.log('📸 [ScreenshotGenerator] Starting canvas-only screenshot capture...');
    
    // First, locate the main canvas element
    const canvasElement = document.querySelector('.canvas');
    
    if (!canvasElement) {
      console.error('📸 [ScreenshotGenerator] Could not find canvas element');
      return null;
    }
    
    // Log canvas dimensions and position for debugging
    const canvasRect = canvasElement.getBoundingClientRect();
    console.log('📸 [ScreenshotGenerator] Canvas dimensions:', {
      width: canvasRect.width,
      height: canvasRect.height,
      top: canvasRect.top,
      left: canvasRect.left,
      right: canvasRect.right,
      bottom: canvasRect.bottom,
      absoluteX: window.scrollX + canvasRect.left,
      absoluteY: window.scrollY + canvasRect.top
    });
    
    // Get all images in the canvas area
    const canvasImages = canvasElement.querySelectorAll('img');
    console.log(`📸 [ScreenshotGenerator] Found ${canvasImages.length} images in canvas area`);
    
    // Create a map to store original image sources and their proxied versions
    const originalToProxiedImages = new Map();
    
    // Pre-load all images before capturing
    const imageLoadPromises = [];
    
    canvasImages.forEach((img, index) => {
      console.log(`📸 [ScreenshotGenerator] Image ${index} src:`, img.src ? img.src.substring(0, 80) + '...' : 'no src');
      
      // CORS Workaround: If this is a Firebase Storage image, try to proxy it
      if (img.src && img.src.includes('firebasestorage.googleapis.com')) {
        // Save original src
        const originalSrc = img.src;
        originalToProxiedImages.set(img, originalSrc);
        
        // Option 1: Try to use a CORS proxy (not recommended for production)
        // const proxyUrl = `https://cors-anywhere.herokuapp.com/${originalSrc}`;
        
        // Option 2: Use an image proxy service that adds CORS headers
        // const proxyUrl = `https://images.weserv.nl/?url=${encodeURIComponent(originalSrc)}`;
        
        // Option 3: Create a canvas copy of the image if it's already loaded
        if (img.complete && img.naturalWidth > 0) {
          try {
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = img.naturalWidth;
            tempCanvas.height = img.naturalHeight;
            const ctx = tempCanvas.getContext('2d');
            
            // Draw a placeholder with similar dimensions 
            ctx.fillStyle = '#f0f0f0';
            ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
            
            // Try to draw the image anyway - this may fail due to CORS
            try {
              ctx.drawImage(img, 0, 0);
            } catch (e) {
              console.warn(`📸 [ScreenshotGenerator] Could not draw image ${index} to canvas due to CORS`);
            }
            
            // Apply a label
            ctx.fillStyle = '#888888';
            ctx.font = '14px Arial';
            ctx.textAlign = 'center';
            ctx.fillText('Image', tempCanvas.width/2, tempCanvas.height/2);
            
            // Use the data URL instead
            img.crossOrigin = 'anonymous';
            img.src = tempCanvas.toDataURL('image/png');
            console.log(`📸 [ScreenshotGenerator] Created placeholder for Firebase image ${index}`);
          } catch (canvasError) {
            console.error(`📸 [ScreenshotGenerator] Error creating canvas for image ${index}:`, canvasError);
          }
        }
      }
      
      // Create a promise for this image loading (original code remains)
      const loadPromise = new Promise((resolve) => {
        // Skip if the image is already loaded completely
        if (img.complete && img.naturalWidth !== 0) {
          console.log(`📸 [ScreenshotGenerator] Image ${index} already loaded`);
          resolve();
          return;
        }
        
        // Set up load and error handlers
        img.onload = () => {
          console.log(`📸 [ScreenshotGenerator] Image ${index} loaded successfully`);
          resolve();
        };
        
        img.onerror = () => {
          console.warn(`📸 [ScreenshotGenerator] Image ${index} failed to load, using placeholder`);
          
          // Create a placeholder instead of just resolving
          try {
            const width = img.width || img.offsetWidth || 200;
            const height = img.height || img.offsetHeight || 150;
            
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = width;
            tempCanvas.height = height;
            const ctx = tempCanvas.getContext('2d');
            
            // Draw placeholder
            ctx.fillStyle = '#f0f0f0';
            ctx.fillRect(0, 0, width, height);
            ctx.fillStyle = '#888888';
            ctx.font = '14px Arial';
            ctx.textAlign = 'center';
            ctx.fillText('Image', width/2, height/2);
            
            // Replace with placeholder
            img.src = tempCanvas.toDataURL();
          } catch (e) {
            console.warn(`📸 [ScreenshotGenerator] Error creating placeholder:`, e);
          }
          
          resolve();
        };
        
        // If the image is Firebase Storage image with potential CORS issues
        // Add a timestamp to force a fresh load but keep the original URL structure
        if (img.src && img.src.includes('firebasestorage.googleapis.com') && !originalToProxiedImages.has(img)) {
          // Replace Firebase Storage images with placeholders when they're already loaded
          // to avoid trying to draw potentially tainted images
          if (img.complete && img.naturalWidth > 0) {
            try {
              // Create a safe placeholder that matches the image dimensions
              const width = img.naturalWidth || img.width || 200;
              const height = img.naturalHeight || img.height || 150;
              
              // Create a new canvas for the placeholder
              const placeholderCanvas = document.createElement('canvas');
              placeholderCanvas.width = width;
              placeholderCanvas.height = height;
              const pCtx = placeholderCanvas.getContext('2d');
              
              // Draw a nice placeholder with matching dimensions
              pCtx.fillStyle = '#f5f5f5';
              pCtx.fillRect(0, 0, width, height);
              
              // Add a border
              pCtx.strokeStyle = '#e0e0e0';
              pCtx.lineWidth = 2;
              pCtx.strokeRect(2, 2, width-4, height-4);
              
              // Add an image icon
              pCtx.fillStyle = '#999999';
              pCtx.font = `${Math.max(16, Math.min(width/10, 32))}px Arial`;
              pCtx.textAlign = 'center';
              pCtx.textBaseline = 'middle';
              pCtx.fillText('IMAGE', width/2, height/2);
              
              // Create a new safe image
              const safeImg = new Image();
              safeImg.width = width;
              safeImg.height = height;
              safeImg.src = placeholderCanvas.toDataURL('image/png');
              
              // Replace the original image with the placeholder
              img.parentNode.replaceChild(safeImg, img);
              console.log(`📸 [ScreenshotGenerator] Replaced Firebase image ${index} with safe placeholder`);
            } catch (e) {
              console.warn(`📸 [ScreenshotGenerator] Could not create placeholder for image ${index}:`, e);
              
              // Force a reload with cache busting as fallback
              const cacheBuster = `cache=${Date.now()}`;
              const separator = img.src.includes('?') ? '&' : '?';
              img.crossOrigin = 'anonymous'; // Try with anonymous first
              img.src = `${img.src}${separator}${cacheBuster}`;
              console.log(`📸 [ScreenshotGenerator] Added cache buster to image ${index}`);
            }
          } else {
            // Force a reload with cache busting
            const cacheBuster = `cache=${Date.now()}`;
            const separator = img.src.includes('?') ? '&' : '?';
            img.crossOrigin = 'anonymous'; // Try with anonymous first
            img.src = `${img.src}${separator}${cacheBuster}`;
            console.log(`📸 [ScreenshotGenerator] Added cache buster to image ${index}`);
          }
        }
      });
      
      imageLoadPromises.push(loadPromise);
    });
    
    // Wait for all images to load or timeout after 3 seconds  
    console.log(`📸 [ScreenshotGenerator] Waiting for ${imageLoadPromises.length} images to load...`);
    await Promise.race([
      Promise.all(imageLoadPromises),
      new Promise(resolve => setTimeout(() => {
        console.warn('📸 [ScreenshotGenerator] Image loading timed out after 3 seconds');
        resolve();
      }, 3000))
    ]);
    
    // Additional delay to ensure rendering is complete
    await new Promise(resolve => setTimeout(resolve, 500));
    console.log('📸 [ScreenshotGenerator] All images processed, preparing to capture...');
    
    // Create a clone of the canvas for capturing to avoid modifying the original
    const tempContainer = document.createElement('div');
    tempContainer.style.position = 'absolute';
    tempContainer.style.left = '-9999px';
    tempContainer.style.width = `${canvasRect.width}px`;
    tempContainer.style.height = `${canvasRect.height}px`;
    tempContainer.style.overflow = 'hidden';
    document.body.appendChild(tempContainer);
    
    // Clone the canvas with all its content
    const canvasClone = canvasElement.cloneNode(true);
    canvasClone.style.position = 'relative';
    canvasClone.style.left = '0';
    canvasClone.style.top = '0';
    tempContainer.appendChild(canvasClone);
    
    // Force layout calculation to ensure clone is properly rendered
    void tempContainer.offsetHeight;
    
    // Try using html2canvas with optimized settings for images
    try {
      console.log('📸 [ScreenshotGenerator] Starting html2canvas capture of cloned canvas...');
      console.time('canvasCapture');
      
      const canvas = await html2canvas(canvasClone, {
        width: canvasRect.width,
        height: canvasRect.height,
        scale: 2, // Higher scale for better quality
        useCORS: true,
        allowTaint: true,
        backgroundColor: getComputedStyle(canvasElement).backgroundColor || '#ffffff',
        logging: true,
        imageTimeout: 5000, // Longer timeout for images
        onclone: (documentClone) => {
          console.log('📸 [ScreenshotGenerator] Processing cloned document...');
          
          // Find all images in the clone and ensure they're visible
          const clonedImages = documentClone.querySelectorAll('img');
          clonedImages.forEach((img, i) => {
            // For Firebase Storage images, try to make them work
            if (img.src && img.src.includes('firebasestorage.googleapis.com')) {
              img.crossOrigin = 'anonymous';
              
              // Create a backup plan if image doesn't load in the clone
              img.onerror = () => {
                // Find the corresponding original image
                const originalImage = canvasImages[i];
                if (originalImage && originalImage.complete && originalImage.naturalWidth > 0) {
                  // Try to manually copy the pixels from the original to a canvas
                  try {
                    const tempCanvas = document.createElement('canvas');
                    tempCanvas.width = originalImage.naturalWidth;
                    tempCanvas.height = originalImage.naturalHeight;
                    const ctx = tempCanvas.getContext('2d');
                    ctx.drawImage(originalImage, 0, 0);
                    img.src = tempCanvas.toDataURL();
                  } catch (e) {
                    console.warn('📸 [ScreenshotGenerator] Failed to copy original image pixels', e);
                  }
                }
              };
            }
          });
        }
      });
      
      console.timeEnd('canvasCapture');
      
      // Clean up the temporary container
      document.body.removeChild(tempContainer);
      
      // Convert to data URL with high quality
      const dataUrl = canvas.toDataURL('image/png', 0.95);
      console.log('📸 [ScreenshotGenerator] Canvas screenshot completed. Size:', dataUrl.length);
      
      return dataUrl;
    } catch (error) {
      console.error('📸 [ScreenshotGenerator] Error capturing canvas screenshot:', error);
      
      // Clean up if the tempContainer was created
      if (tempContainer.parentNode) {
        document.body.removeChild(tempContainer);
      }
      
      // Fallback to direct canvas rendering approach
      try {
        console.log('📸 [ScreenshotGenerator] Attempting direct canvas rendering approach...');
        
        // Create a canvas with the same dimensions
        const fallbackCanvas = document.createElement('canvas');
        fallbackCanvas.width = canvasRect.width * 2;  // Higher quality
        fallbackCanvas.height = canvasRect.height * 2;
        const ctx = fallbackCanvas.getContext('2d');
        
        // Draw background color
        const backgroundColor = getComputedStyle(canvasElement).backgroundColor || '#ffffff';
        ctx.fillStyle = backgroundColor === 'transparent' ? '#ffffff' : backgroundColor;
        ctx.fillRect(0, 0, fallbackCanvas.width, fallbackCanvas.height);
        
        // Try to manually draw each visible element from the original canvas
        const elements = canvasElement.querySelectorAll('[id^="element-"]');
        
        // First pass - draw non-image elements
        elements.forEach(element => {
          const rect = element.getBoundingClientRect();
          const relativeX = (rect.left - canvasRect.left) * 2;
          const relativeY = (rect.top - canvasRect.top) * 2;
          const width = rect.width * 2;
          const height = rect.height * 2;
          
          // Skip images for now - we'll handle them separately
          if (element.querySelector('img')) return;
          
          // Get element background color if possible
          let elementColor = '#e0e0e0';
          try {
            const elementStyle = window.getComputedStyle(element);
            if (elementStyle.backgroundColor && elementStyle.backgroundColor !== 'transparent') {
              elementColor = elementStyle.backgroundColor;
            }
          } catch (e) {}
          
          // Draw element background
          ctx.fillStyle = elementColor;
          ctx.fillRect(relativeX, relativeY, width, height);
          
          // If it's a text element, try to render the text
          if (element.textContent && !element.querySelector('img')) {
            try {
              const elementStyle = window.getComputedStyle(element);
              ctx.fillStyle = elementStyle.color || '#000000';
              ctx.font = `${elementStyle.fontWeight} ${parseInt(elementStyle.fontSize) * 2}px ${elementStyle.fontFamily}`;
              ctx.fillText(element.textContent.trim(), relativeX + 10, relativeY + height/2);
            } catch (e) {
              console.warn('📸 [ScreenshotGenerator] Error rendering text:', e);
            }
          }
        });
        
        // Second pass - try to draw images last (on top)
        canvasImages.forEach(img => {
          if (img.complete && img.naturalWidth > 0) {
            try {
              // Find the image's position relative to the canvas
              const imgRect = img.getBoundingClientRect();
              const relativeX = (imgRect.left - canvasRect.left) * 2;
              const relativeY = (imgRect.top - canvasRect.top) * 2;
              const width = imgRect.width * 2;
              const height = imgRect.height * 2;
              
              // Draw the image
              ctx.drawImage(img, relativeX, relativeY, width, height);
              console.log('📸 [ScreenshotGenerator] Drew image in fallback canvas');
            } catch (e) {
              console.warn('📸 [ScreenshotGenerator] Error drawing image to fallback canvas:', e);
              
              // Draw placeholder instead
              try {
                const imgRect = img.getBoundingClientRect();
                const relativeX = (imgRect.left - canvasRect.left) * 2;
                const relativeY = (imgRect.top - canvasRect.top) * 2;
                const width = imgRect.width * 2;
                const height = imgRect.height * 2;
                
                // Draw a nice placeholder
                ctx.fillStyle = '#f5f5f5';
                ctx.fillRect(relativeX, relativeY, width, height);
                
                // Add a border
                ctx.strokeStyle = '#e0e0e0';
                ctx.lineWidth = 2;
                ctx.strokeRect(relativeX+2, relativeY+2, width-4, height-4);
                
                // Label as image
                ctx.fillStyle = '#999999';
                ctx.font = `${Math.max(16, Math.min(width/10, 32))}px Arial`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText('IMAGE', relativeX + width/2, relativeY + height/2);
              } catch (placeholderError) {
                console.warn('📸 [ScreenshotGenerator] Error drawing placeholder:', placeholderError);
              }
            }
          }
        });
        
        // Convert to data URL
        const dataUrl = fallbackCanvas.toDataURL('image/png', 0.95);
        console.log('📸 [ScreenshotGenerator] Fallback canvas capture completed');
        return dataUrl;
      } catch (fallbackError) {
        console.error('📸 [ScreenshotGenerator] Fallback canvas capture failed:', fallbackError);
        
        // Super simplified fallback - create a basic thumbnail representing the canvas 
        return createSimpleFallbackCanvas(canvasElement, canvasRect);
      }
    }
  } catch (error) {
    console.error('📸 [ScreenshotGenerator] Critical error in canvas screenshot:', error);
    return null;
  }
}

// Helper function to create a very simple fallback canvas as last resort
function createSimpleFallbackCanvas(canvasElement, canvasRect) {
  try {
    console.log('📸 [ScreenshotGenerator] Creating guaranteed simple fallback thumbnail');
    
    // Standard thumbnail dimensions
    const width = 600;
    const height = 450;
    
    const simpleCanvas = document.createElement('canvas');
    simpleCanvas.width = width;
    simpleCanvas.height = height;
    const ctx = simpleCanvas.getContext('2d');
    
    // Attempt to get canvas color, default to a nice gradient if we can't
    let backgroundColor;
    try {
      backgroundColor = getComputedStyle(canvasElement).backgroundColor;
      if (!backgroundColor || backgroundColor === 'transparent') {
        backgroundColor = '#ffffff';
      }
    } catch (e) {
      backgroundColor = '#ffffff';
    }
    
    // Create a gradient background
    const gradient = ctx.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, backgroundColor);
    gradient.addColorStop(1, adjustColor(backgroundColor, -20)); // Slightly darker version
    
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
    
    // Add a subtle border
    ctx.strokeStyle = adjustColor(backgroundColor, -40); // Even darker for border
    ctx.lineWidth = 8;
    ctx.strokeRect(10, 10, width - 20, height - 20);
    
    // Add decoration
    drawDecorations(ctx, width, height, backgroundColor);
    
    // Add text
    ctx.fillStyle = getContrastingColor(backgroundColor);
    ctx.font = 'bold 32px Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Template Preview', width/2, height/2 - 15);
    
    ctx.font = '18px Arial, sans-serif';
    ctx.fillText('Generated Thumbnail', width/2, height/2 + 20);
    
    const timestamp = new Date().toLocaleTimeString();
    ctx.font = '14px Arial, sans-serif';
    ctx.fillText(timestamp, width/2, height - 30);
    
    // Convert to data URL - try PNG first
    try {
      const dataUrl = simpleCanvas.toDataURL('image/png', 0.95);
      console.log('📸 [ScreenshotGenerator] PNG fallback created successfully');
      return dataUrl;
    } catch (pngError) {
      // If PNG fails, try JPEG
      try {
        console.warn('📸 [ScreenshotGenerator] PNG fallback failed, trying JPEG:', pngError);
        const dataUrl = simpleCanvas.toDataURL('image/jpeg', 0.9);
        console.log('📸 [ScreenshotGenerator] JPEG fallback created successfully');
        return dataUrl;
      } catch (jpegError) {
        console.error('📸 [ScreenshotGenerator] JPEG fallback also failed:', jpegError);
        
        // Last resort - return a pre-encoded base64 image string
        return getHardcodedFallbackImage();
      }
    }
  } catch (e) {
    console.error('📸 [ScreenshotGenerator] Creating fallback failed:', e);
    return getHardcodedFallbackImage();
  }
}

// Helper to get a contrasting text color for readability
function getContrastingColor(color) {
  // Default to black if we can't parse the color
  if (!color || color === 'transparent') {
    return '#000000';
  }
  
  try {
    // Extract RGB components
    let r, g, b;
    
    if (color.startsWith('#')) {
      // Hex format
      const hex = color.replace('#', '');
      if (hex.length === 3) {
        r = parseInt(hex[0] + hex[0], 16);
        g = parseInt(hex[1] + hex[1], 16);
        b = parseInt(hex[2] + hex[2], 16);
      } else {
        r = parseInt(hex.substr(0, 2), 16);
        g = parseInt(hex.substr(2, 2), 16);
        b = parseInt(hex.substr(4, 2), 16);
      }
    } else if (color.startsWith('rgb')) {
      // RGB or RGBA format
      const matches = color.match(/(\d+),\s*(\d+),\s*(\d+)/);
      if (matches && matches.length >= 4) {
        r = parseInt(matches[1]);
        g = parseInt(matches[2]);
        b = parseInt(matches[3]);
      } else {
        return '#000000';
      }
    } else {
      return '#000000';
    }
    
    // Calculate relative luminance using the formula
    // Luminance = 0.299*R + 0.587*G + 0.114*B
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Use white for dark colors, black for light colors
    return luminance > 0.5 ? '#000000' : '#ffffff';
  } catch (e) {
    return '#000000'; // Default to black on error
  }
}

// Helper to adjust color brightness
function adjustColor(color, amount) {
  try {
    // Default color
    if (!color || color === 'transparent') {
      color = '#ffffff';
    }
    
    let r, g, b;
    
    if (color.startsWith('#')) {
      // Hex format
      const hex = color.replace('#', '');
      if (hex.length === 3) {
        r = parseInt(hex[0] + hex[0], 16);
        g = parseInt(hex[1] + hex[1], 16);
        b = parseInt(hex[2] + hex[2], 16);
      } else {
        r = parseInt(hex.substr(0, 2), 16);
        g = parseInt(hex.substr(2, 2), 16);
        b = parseInt(hex.substr(4, 2), 16);
      }
    } else if (color.startsWith('rgb')) {
      // RGB or RGBA format
      const matches = color.match(/(\d+),\s*(\d+),\s*(\d+)/);
      if (matches && matches.length >= 4) {
        r = parseInt(matches[1]);
        g = parseInt(matches[2]);
        b = parseInt(matches[3]);
      } else {
        r = g = b = 255; // Default to white
      }
    } else {
      r = g = b = 255; // Default to white
    }
    
    // Adjust the color brightness
    r = Math.max(0, Math.min(255, r + amount));
    g = Math.max(0, Math.min(255, g + amount));
    b = Math.max(0, Math.min(255, b + amount));
    
    // Convert back to hex
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
  } catch (e) {
    return color; // Return original on error
  }
}

// Add decorative elements to the fallback thumbnail
function drawDecorations(ctx, width, height, backgroundColor) {
  try {
    // Draw some shapes to make it look more like a template
    
    // Use a contrasting color for decorations
    const contrastColor = getContrastingColor(backgroundColor);
    const alpha = '33'; // 20% opacity in hex
    
    // Draw a few circles
    ctx.fillStyle = contrastColor + alpha;
    ctx.beginPath();
    ctx.arc(width * 0.2, height * 0.3, 40, 0, Math.PI * 2);
    ctx.fill();
    
    ctx.beginPath();
    ctx.arc(width * 0.8, height * 0.7, 60, 0, Math.PI * 2);
    ctx.fill();
    
    // Draw some rectangles representing text blocks
    ctx.fillStyle = contrastColor + '22'; // 13% opacity
    ctx.fillRect(width * 0.25, height * 0.6, width * 0.5, 15);
    ctx.fillRect(width * 0.3, height * 0.65, width * 0.4, 15);
    ctx.fillRect(width * 0.35, height * 0.7, width * 0.3, 15);
  } catch (e) {
    console.warn('📸 [ScreenshotGenerator] Error drawing decorations:', e);
    // Fail silently - decorations are not critical
  }
}

// Ultimate fallback - pre-encoded minimal image if all else fails
function getHardcodedFallbackImage() {
  console.log('📸 [ScreenshotGenerator] Using hardcoded fallback image');
  
  // This is a small, base64-encoded PNG image (1x1 transparent pixel)
  // We use this as an absolute last resort if every other method fails
  return 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+P+/HgAFdQIQ1Ak2AwAAAABJRU5ErkJggg==';
} 